import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { catchError, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { CertificatesService } from '../services/api/certificates.service';
import { TagsService } from '../services/api/tags.service';
import { FeathersService } from '../services/api/feathers.service';
import { AuthService } from '../services/api/auth.service';
import { CertificateStatus, QueryParamsKeys } from '../enums';
import { TagRequest } from '../models/tag';

export const certificatePrivacyGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const certificatesService = inject(CertificatesService);
  const tagsService = inject(TagsService);
  const feathers = inject(FeathersService);
  const authService = inject(AuthService);

  // Try to get id from current route
  let certificateId = route.paramMap.get('id');
  const tagId = route.queryParamMap.get('tagID');

  // Debug logging
  console.log('CertificatePrivacyGuard - route params:', {
    id: certificateId,
    tagID: tagId,
    allParams: route.params,
    paramMapKeys: Object.keys(route.params || {}),
  });

  // If there's no certificateId or tagId, allow access
  if (!certificateId && !tagId) {
    return of(true);
  }

  // Validate certificateId - must be a non-empty string
  if (certificateId) {
    const trimmedId = certificateId.trim();
    if (!trimmedId || trimmedId === 'undefined' || trimmedId === 'null' || trimmedId.length === 0) {
      console.error('CertificatePrivacyGuard - Invalid certificateId:', certificateId);
      const currentUrl = state.url.split('?')[0]; // Get URL without query params
      return of(router.createUrlTree(['/certificate-private'], {
        queryParams: { [QueryParamsKeys.NextNavigation]: currentUrl }
      }));
    }
    certificateId = trimmedId; // Use trimmed version
  }

  // Wait for initialization and get current user
  return feathers.initialized$.pipe(
    filter((initialized) => initialized),
    take(1),
    switchMap(() => {
      const currentUser$ = feathers.me$.pipe(take(1));

      // If certificateId exists and is valid, load certificate via CertificatesService
      if (certificateId && certificateId.trim().length > 0) {
        console.log('CertificatePrivacyGuard - Loading certificate with ID:', certificateId);
        return certificatesService
          .getPublicCertificatePreview(certificateId, true)
          .pipe(
            switchMap((certificate) => {
              if (!certificate) {
                const currentUrl = state.url.split('?')[0]; // Get URL without query params
                return of(router.createUrlTree(['/certificate-private'], {
                  queryParams: { [QueryParamsKeys.NextNavigation]: currentUrl }
                }));
              }

              return currentUser$.pipe(
                map((currentUser) => {
                  const isCreated = certificate.status === CertificateStatus.Created;
                  const isAuthenticated = authService.isUserAuthenticated;
                  const isOwner = currentUser?._id === certificate.owner?._id;

                  // If certificate has Created status, only allow access if user is authenticated and is the owner
                  if (isCreated) {
                    if (!isAuthenticated || !isOwner) {
                      const currentUrl = state.url.split('?')[0]; // Get URL without query params
                      return router.createUrlTree(['/certificate-private'], {
                        queryParams: { [QueryParamsKeys.NextNavigation]: currentUrl }
                      });
                    }
                  }

                  return true;
                })
              );
            }),
            catchError((error) => {
              console.error('Certificate privacy guard error:', error);
              // If loading error, redirect to certificate-private page
              const currentUrl = state.url.split('?')[0]; // Get URL without query params
              return of(router.createUrlTree(['/certificate-private'], {
                queryParams: { [QueryParamsKeys.NextNavigation]: currentUrl }
              }));
            })
          );
      }

      // If tagId exists, load via TagsService
      if (tagId) {
        const tagRequest: TagRequest = {
          tagId: tagId,
          tac: route.queryParamMap.get('tac') || undefined,
        };

        return tagsService.readTag(tagRequest, false).pipe(
          take(1),
          switchMap((tagResponse) => {
            const certificate = tagResponse?.certificate;

            if (!certificate) {
              return of(true); // Allow access if no certificate
            }

            return currentUser$.pipe(
              map((currentUser) => {
                const isCreated = certificate.status === CertificateStatus.Created;
                const isAuthenticated = authService.isUserAuthenticated;
                const isOwner = currentUser?._id === certificate.owner?._id;

                // If certificate has Created status, only allow access if user is authenticated and is the owner
                if (isCreated) {
                  if (!isAuthenticated || !isOwner) {
                    const currentUrl = state.url.split('?')[0]; // Get URL without query params
                    return router.createUrlTree(['/certificate-private'], {
                      queryParams: { [QueryParamsKeys.NextNavigation]: currentUrl }
                    });
                  }
                }

                return true;
              })
            );
          }),
          catchError(() => {
            // If loading error, redirect to certificate-private page
            const currentUrl = state.url.split('?')[0];
            return of(router.createUrlTree(['/certificate-private'], {
              queryParams: { [QueryParamsKeys.NextNavigation]: currentUrl }
            }));
          })
        );
      }

      return of(true);
    })
  );
};
